import { PLAN } from '@/common/constants/markets';
import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import {
  getFlexibleBenefitsBenefitSummary,
  getFlexibleBenefitsPayrollSummary,
  getFlexibleBenefitsRollover,
  getFlexibleBenefitsRolloverSummary,
  getFlexibleBenefitsSummary,
  updateFlexibleBenefitsRollover,
} from '@/common/integrations/menhir/employee/flexible-benefits/flexible-benefits.service';
import {
  TFlexibleBenefitRollover,
  TFlexibleBenefitsBenefitSummary,
  TFlexibleBenefitsPayrollSummary,
  TFlexibleBenefitsRolloverSummary,
  TFlexibleBenefitsSummary,
  TGetFlexibleBenefitsPayrollSummaryFilters,
} from '@/common/integrations/menhir/employee/flexible-benefits/flexible-benefits.types';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { IMenhirBenefitSlug } from '../benefits/benefits.types';

export function useGetFlexibleBenefitsSummary(
  options?: UseQueryOptions<TFlexibleBenefitsSummary, any>
) {
  const { data: summary, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.FLEXIBLE_BENEFITS.SUMMARY],
    queryFn: getFlexibleBenefitsSummary,
    ...options,
  });

  return { summary, ...rest };
}

export function useGetFlexibleBenefitsPayrollSummary(
  filters?: TGetFlexibleBenefitsPayrollSummaryFilters,
  options?: UseQueryOptions<TFlexibleBenefitsPayrollSummary>
) {
  const { data: summary, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.FLEXIBLE_BENEFITS.PAYROLL.SUMMARY,
      JSON.stringify(filters),
    ],
    queryFn: () => getFlexibleBenefitsPayrollSummary(filters),
    ...options,
  });

  return { summary, ...rest };
}

export function useGetFlexibleBenefitsBenefitSummary(
  slug: IMenhirBenefitSlug,
  options?: UseQueryOptions<TFlexibleBenefitsBenefitSummary, any>
) {
  const { data: benefitSummary, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.FLEXIBLE_BENEFITS.BENEFITS.SUMMARY,
      slug,
    ],
    queryFn: () => getFlexibleBenefitsBenefitSummary(slug),
    ...options,
  });

  return { benefitSummary, ...rest };
}

export function useGetFlexibleBenefitsRollover(
  slug: IMenhirBenefitSlug,
  options?: UseQueryOptions<TFlexibleBenefitRollover>
) {
  const { data: rollover, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.FLEXIBLE_BENEFITS.BENEFITS.ROLLOVER,
      slug,
    ],
    queryFn: () => getFlexibleBenefitsRollover(slug),
    ...options,
  });

  return { rollover, ...rest };
}

export function useUpdateFlexibleBenefitsRollover(
  options?: UseMutationOptions<
    any,
    unknown,
    {
      slug: IMenhirBenefitSlug;
      active: boolean;
    }
  >
) {
  return useMutation({
    mutationFn: ({ slug, active }) =>
      updateFlexibleBenefitsRollover(slug, active),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.FLEXIBLE_BENEFITS.BENEFITS.ROLLOVER,
        slug,
      ]);
    },
    ...options,
  });
}

export function useGetFlexibleBenefitsRolloverSummary(
  options?: UseQueryOptions<TFlexibleBenefitsRolloverSummary>
) {
  const { data: rollover, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.FLEXIBLE_BENEFITS.ROLLOVER],
    queryFn: getFlexibleBenefitsRolloverSummary,
    ...options,
  });

  return {
    rollover,
    ...rest,
  };
}

export function useEmployeeHasFlexibleBenefits() {
  const { company } = useEmployeeDetails();
  return (
    company.settings?.plan === PLAN.ES_WALLET ||
    company.settings?.plan === PLAN.ES_HYBRID
  );
}
